import React from 'react';
import { Variant } from 'types/cx-editor';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { MOCK_TEMPLATES_RESPONSE } from 'lib/asset-templates';
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  Menu,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Save,
  GitCompareArrows,
  FileClock,
  ClipboardCheck,
  Monitor,
  Smartphone,
  Network,
  Languages,
  Layers,
  Library,
  ChevronDown
} from 'lucide-react';

// Mock clusters - matching Studio version for apple-to-apple comparison
// Phase 2: Will be replaced with API data
const MOCK_CLUSTERS_WYSIWYG = [
  { id: "cluster_001-MOCK", name: "EE Treatment" },
  { id: "cluster_002", name: "AB Test - $ in Subject" },
  { id: "cluster_003", name: "AB Test - No $ in Subject" },
];

// Mock locales - matching Studio version
const MOCK_LOCALES = ['en_US', 'es_US'];

// EditorToolbar no longer makes API calls - moved to WysiwygPage to prevent duplicates

type Props = {
  templateKey: string;
  elementKey: string;
  variant: Variant;
  channel?: string;
  previewMode?: 'desktop' | 'mobile';
  onElementChange: (k: string) => void;
  onVariantChange: (v: Variant) => void;
  onPreviewModeChange?: (mode: 'desktop' | 'mobile') => void;
  onSave?: () => Promise<void> | void;
  onOpenCompare?: () => void;
  onCancel?: () => void;
  renderOnlyUpperSection?: boolean;
  renderOnlyLowerSection?: boolean;
  availableElements?: Array<{ elementKey: string; displayName: string }>; // Elements parsed from rendered HTML (optional)
  availableScenarios?: Array<{ label: string; value: string }>; // Scenarios loaded by parent (WysiwygPage)
  scenariosLoading?: boolean; // Loading state from parent
  onRenderComplete?: (templateKey: string) => void; // Callback when render completes to trigger template/scenario fetch
  modifiedCount?: number; // Number of modified fields (for Save button counter)
}

export const EditorToolbar: React.FC<Props> = ({ 
  templateKey, 
  elementKey, 
  variant, 
  channel = 'Email',
  previewMode = 'desktop',
  onElementChange, 
  onVariantChange,
  onPreviewModeChange,
  onSave, 
  onOpenCompare,
  onCancel,
  renderOnlyUpperSection = false,
  renderOnlyLowerSection = false,
  availableElements,
  availableScenarios = [],
  scenariosLoading: scenariosLoadingProp = false,
  onRenderComplete,
  modifiedCount = 0
}) => {
  const { rc } = useGlobalConfig();
  const [elements, setElements] = React.useState<{ elementKey: string; displayName: string }[]>([]);
  const [scenariosError, setScenariosError] = React.useState<string | null>(null);
  
  // Use scenarios from props (loaded by WysiwygPage)
  const scenarios = availableScenarios.length > 0 ? availableScenarios : [{ label: 'default', value: 'default' }];
  const scenariosLoading = scenariosLoadingProp;
  const [saving, setSaving] = React.useState(false);
  const selectedPilotId = useSelector(selectSelectedPilotId as any) as string;
  const [clusters, setClusters] = React.useState<string[]>([]);
  const [locales, setLocales] = React.useState<string[]>(MOCK_LOCALES);
  const [hierarchyMenuOpen, setHierarchyMenuOpen] = React.useState(false);
  const [hierarchyAnchorEl, setHierarchyAnchorEl] = React.useState<null | HTMLElement>(null);
  const [clusterMenuOpen, setClusterMenuOpen] = React.useState(false);

  // Update elements from props if provided (this doesn't require API call)
  React.useEffect(() => {
    if (availableElements && availableElements.length > 0) {
      setElements(availableElements);
    }
  }, [availableElements]);
  
  // EditorToolbar no longer fetches template meta - it's done in WysiwygPage to prevent duplicates
  // Scenarios are passed via props/callback from WysiwygPage

  React.useEffect(() => {
    let ignore = false;
    async function loadClusters() {
      // Use mock data for now (matching Studio behavior)
      // Phase 2: Replace with API call
      if (!ignore) {
        const mockClusterNames = MOCK_CLUSTERS_WYSIWYG.map(c => c.name);
        setClusters(mockClusterNames);
      }
      
      // TODO: Phase 2 - Uncomment when ready to use real API
      // try {
      //   if (!selectedPilotId) return;
      //   const resp = await rc.bffClient.getClusters(String(selectedPilotId));
      //   const list = (resp as any)?.data?.clusters || (resp as any)?.data || [];
      //   const names: string[] = Array.isArray(list) ? list.map((c: any) => c.name || c.cluster_name || c.clusterName || String(c)) : [];
      //   if (!ignore) setClusters(names);
      // } catch {
      //   if (!ignore) setClusters([]);
      // }
    }
    loadClusters();
    return () => { ignore = true; };
  }, [selectedPilotId, rc]);

  // Load supported locales - using mock data for now (matching Studio behavior)
  // Phase 2: Replace with API call
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      // Use mock locales (matching Studio: en_US, es_US)
        if (!ignore) {
        setLocales(MOCK_LOCALES);
        if (!MOCK_LOCALES.includes(variant.locale)) {
          onVariantChange({ ...variant, locale: MOCK_LOCALES[0] });
        }
      }
      
      // TODO: Phase 2 - Uncomment when ready to use real API
      // if (!selectedPilotId) return;
      // try {
      //   const resp = await rc.bffClient.getProjectLocales(String(selectedPilotId));
      //   const list = (resp as any)?.data?.locales || MOCK_LOCALES;
      //   if (!ignore) {
      //     setLocales(list);
      //     if (!list.includes(variant.locale)) {
      //       onVariantChange({ ...variant, locale: list[0] });
      //     }
      //   }
      // } catch {
      //   if (!ignore) {
      //     setLocales(MOCK_LOCALES);
      //     if (!MOCK_LOCALES.includes(variant.locale)) {
      //       onVariantChange({ ...variant, locale: MOCK_LOCALES[0] });
      //     }
      //   }
      // }
    })();
    return () => { ignore = true; };
  }, [selectedPilotId, rc]);

  // Get template name and channel from mock data
  const { templateName, templateChannel } = React.useMemo(() => {
    if (!templateKey) return { templateName: '', templateChannel: channel };
    for (const ch of MOCK_TEMPLATES_RESPONSE.channels) {
      const found = ch.templates.find(t => t.templateKey === templateKey);
      if (found) {
        return { templateName: found.name, templateChannel: ch.name };
      }
    }
    return { templateName: templateKey, templateChannel: channel };
  }, [templateKey, channel]);

  const prefixMatch = templateName.match(/\(([^)]+)\)/);
  const prefix = prefixMatch ? prefixMatch[0] : '';
  const nameWithoutPrefix = templateName.replace(prefix, '').trim();
  const displayPrefix = prefix.replace(/[()]/g, '');

  const hierarchyValue = variant.hierarchy.startsWith('Cluster:') 
    ? variant.hierarchy.replace('Cluster:', '') 
    : variant.hierarchy;
  const hierarchyDisplayText = hierarchyValue === 'Project' ? 'Project' : `Cluster: ${hierarchyValue}`;
  const selectedScenarioLabel = scenarios.find(s => s.value === variant.scenario)?.label || variant.scenario;

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleHierarchyClick = (event: React.MouseEvent<HTMLElement>) => {
    setHierarchyAnchorEl(event.currentTarget);
    setHierarchyMenuOpen(true);
  };

  const handleHierarchyClose = () => {
    setHierarchyMenuOpen(false);
    setClusterMenuOpen(false);
    setHierarchyAnchorEl(null);
  };

  const handleProjectSelect = () => {
    onVariantChange({ ...variant, hierarchy: 'Project' });
    handleHierarchyClose();
  };

  const handleClusterSelect = (clusterName: string) => {
    onVariantChange({ ...variant, hierarchy: `Cluster:${clusterName}` });
    handleHierarchyClose();
  };

  const elementDropdownTitle = React.useMemo(() => {
    switch (templateChannel) {
      case 'HER': return 'HER Modules';
      case 'Web': return 'Web Widgets';
      case 'Email':
      default:
        return 'Email Elements';
    }
  }, [templateChannel]);

  // If only rendering upper section, return just that
  if (renderOnlyUpperSection) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleHierarchyClick}
            endIcon={<ChevronDown size={16} />}
            startIcon={<Network size={16} />}
            sx={{
              height: 32, // h-8 = 32px
              minWidth: 180, // w-[180px]
              fontSize: '0.875rem',
              textTransform: 'none',
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                color: 'text.secondary'
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
                opacity: 0.5
              }
            }}
          >
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncateText(hierarchyDisplayText, 20)}
            </Typography>
          </Button>
          <Menu
            anchorEl={hierarchyAnchorEl}
            open={hierarchyMenuOpen}
            onClose={handleHierarchyClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                maxHeight: 400
              }
            }}
            MenuListProps={{
              sx: {
                minWidth: 200,
                py: 0.5,
                maxHeight: 400,
                overflow: 'auto',
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem 
              onClick={handleProjectSelect} 
              selected={hierarchyValue === 'Project'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                minHeight: 40,
                fontSize: '0.875rem'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  margin: 0,
                  padding: 0,
                  width: '100%'
                }}
              >
                Project
              </Typography>
            </MenuItem>
            <Divider />
            <Tooltip title="Coming soon">
              <MenuItem 
                onClick={() => {}} // No-op: functionality preserved but disabled
                onMouseEnter={() => {}} // No-op: prevent cluster menu from opening
                disabled={true}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  fontSize: '0.875rem',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    margin: 0,
                    padding: 0,
                    flex: 1
                  }}
                >
                  Clusters (Coming soon)
                </Typography>
                <ChevronDown size={16} style={{ marginLeft: '8px', opacity: 0.7 }} />
              </MenuItem>
            </Tooltip>
            {clusterMenuOpen && clusters.length > 0 && (
              <Menu
                open={clusterMenuOpen}
                anchorEl={hierarchyAnchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClose={() => setClusterMenuOpen(false)}
                PaperProps={{
                  sx: {
                    maxHeight: 400,
                    mt: 0.5,
                    ml: 0.5,
                    boxShadow: 2
                  }
                }}
                MenuListProps={{
                  sx: {
                    minWidth: 200,
                    py: 0.5,
                    maxHeight: 400,
                    overflow: 'auto',
                    '& .MuiMenuItem-root': {
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      fontSize: '0.875rem'
                    }
                  }
                }}
              >
                {clusters.map(c => (
                  <MenuItem 
                    key={c} 
                    onClick={() => handleClusterSelect(c)}
                    selected={hierarchyValue === c}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      fontSize: '0.875rem'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        margin: 0,
                        padding: 0,
                        width: '100%'
                      }}
                    >
                      {c}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Menu>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={variant.locale}
                onChange={(e) => onVariantChange({ ...variant, locale: e.target.value as string })}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  }
                }}
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Languages size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                    <span style={{ fontSize: '0.875rem' }}>{value}</span>
                  </Box>
                )}
              >
                {locales.map(l => (
                  <MenuItem key={l} value={l} sx={{ fontSize: '0.875rem' }}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<GitCompareArrows size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Compare
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<FileClock size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Audit
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<ClipboardCheck size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Checklist
              </Button>
            </span>
          </Tooltip>

          {onCancel && (
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              disabled={modifiedCount === 0}
              sx={{
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: 'error.main',
                ...(modifiedCount === 0 && { opacity: 0.5 })
              }}
            >
              Cancel
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
          onClick={async () => {
            if (!onSave) return;
            try { setSaving(true); await onSave(); } finally { setSaving(false); }
          }}
          disabled={!templateKey || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
            sx={{
              height: 32,
              fontSize: '0.875rem',
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {saving ? 'Saving…' : `Save${modifiedCount > 0 ? ` (${modifiedCount})` : ''}`}
          </Button>
        </Box>
      </Box>
    );
  }

  // If only rendering lower section, return just that
  if (renderOnlyLowerSection) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          flex: 1,
          minWidth: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={templateChannel} 
              size="small"
              sx={{ fontWeight: 600 }}
            />
            {displayPrefix && (
              <Chip
                label={displayPrefix}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  height: 20,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: theme => theme.palette.primary.main + '1A'
                }}
              />
            )}
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {nameWithoutPrefix || templateKey || 'Select a template'}
            </Typography>
          </Box>
          {templateChannel !== 'HER' && onPreviewModeChange && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
                bgcolor: 'background.default'
              }}
            >
              <Button
                variant={previewMode === 'desktop' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('desktop')}
                sx={{
                  minWidth: 28,
                  height: 28,
                  p: 0
                }}
              >
                <Monitor size={16} />
              </Button>
              <Tooltip title="This feature is coming soon">
                <span>
                  <Button
                    variant={previewMode === 'mobile' ? 'contained' : 'text'}
                    size="small"
                    onClick={() => {}} // No-op: functionality preserved but disabled
                    disabled={true}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      p: 0,
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    }}
                  >
                    <Smartphone size={16} />
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={hierarchyDisplayText} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={variant.locale} 
              size="small"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={elementKey}
                onChange={(e) => onElementChange(e.target.value as string)}
                disabled={!elements.length}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  }
                }}
                renderValue={(value) => {
                  const selected = (elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }])
                    .find(e => e.elementKey === value);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Library size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{selected?.displayName || value}</span>
                    </Box>
                  );
                }}
              >
                {(elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }]).map(e => (
                  <MenuItem key={e.elementKey} value={e.elementKey} sx={{ fontSize: '0.875rem' }}>
                    {e.elementKey === 'Full Template' && <Library size={16} style={{ marginRight: '8px' }} />}
                    {e.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              size="small" 
              sx={{ minWidth: 180 }}
              error={!!scenariosError}
            >
              <Select
                value={variant.scenario}
                onChange={(e) => {
                  // Store the scenario VALUE (e.g., "MS_1") in variant.scenario, which will be sent to API
                  // The label (e.g., "Default") is only for display in the UI
                  const selectedValue = e.target.value as string;
                  const selectedScenario = scenarios.find(s => s.value === selectedValue);
                  console.log('[EditorToolbar] Scenario selected:', {
                    label: selectedScenario?.label, // Displayed in UI
                    value: selectedValue, // Stored and sent to API (e.g., "MS_1")
                    willSendToAPI: selectedValue
                  });
                  onVariantChange({ ...variant, scenario: selectedValue });
                }}
                displayEmpty
                disabled={scenariosLoading || scenarios.length === 0}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  }
                }}
                renderValue={(value) => {
                  // Note: 'value' parameter is the scenario VALUE (e.g., "MS_1"), but we display the LABEL (e.g., "Default")
                  if (scenariosLoading) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Loading...</span>
                      </Box>
                    );
                  }
                  const selected = scenarios.find(s => s.value === value);
                  if (!selected && scenarios.length === 0) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Layers size={16} style={{ color: 'inherit', opacity: 0.5 }} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>No scenarios</span>
                      </Box>
                    );
                  }
                  // Display label in UI, but value (e.g., "MS_1") is stored in variant.scenario and sent to API
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{truncateText(selected?.label || value, 14)}</span>
                    </Box>
                  );
                }}
              >
                {scenariosLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Loading scenarios...</Typography>
                    </Box>
                  </MenuItem>
                ) : scenarios.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {scenariosError ? 'Error loading scenarios' : 'No scenarios available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  scenarios.map(s => (
                    <MenuItem 
                      key={s.value} 
                      value={s.value} 
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <Tooltip 
                        title={s.label.length > 20 ? s.label : ''}
                        placement="right"
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap', 
                            fontSize: '0.875rem',
                            width: '100%'
                          }}
                        >
                          {s.label}
                        </Typography>
                      </Tooltip>
                    </MenuItem>
                  ))
                )}
              </Select>
              {scenariosError && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.75 }}>
                  {scenariosError}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>
    );
  }

  // Default: render both sections
  return (
    <>
      {/* Top Toolbar Section - Full Width */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleHierarchyClick}
            endIcon={<ChevronDown size={16} />}
            startIcon={<Network size={16} />}
            sx={{
              height: 32,
              minWidth: 180,
              fontSize: '0.875rem',
              textTransform: 'none',
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                color: 'text.secondary'
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
                opacity: 0.5
              }
            }}
          >
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncateText(hierarchyDisplayText, 20)}
            </Typography>
          </Button>
          <Menu
            anchorEl={hierarchyAnchorEl}
            open={hierarchyMenuOpen}
            onClose={handleHierarchyClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                maxHeight: 400
              }
            }}
            MenuListProps={{
              sx: {
                minWidth: 200,
                py: 0.5,
                maxHeight: 400,
                overflow: 'auto',
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem 
              onClick={handleProjectSelect} 
              selected={hierarchyValue === 'Project'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                minHeight: 40,
                fontSize: '0.875rem'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  margin: 0,
                  padding: 0,
                  width: '100%'
                }}
              >
                Project
              </Typography>
            </MenuItem>
            <Divider />
            <Tooltip title="Coming soon">
              <MenuItem 
                onClick={() => {}} // No-op: functionality preserved but disabled
                onMouseEnter={() => {}} // No-op: prevent cluster menu from opening
                disabled={true}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  fontSize: '0.875rem',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    margin: 0,
                    padding: 0,
                    flex: 1
                  }}
                >
                  Clusters (Coming soon)
                </Typography>
                <ChevronDown size={16} style={{ marginLeft: '8px', opacity: 0.7 }} />
              </MenuItem>
            </Tooltip>
            {clusterMenuOpen && clusters.length > 0 && (
              <Menu
                open={clusterMenuOpen}
                anchorEl={hierarchyAnchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClose={() => setClusterMenuOpen(false)}
                PaperProps={{
                  sx: {
                    maxHeight: 400,
                    mt: 0.5,
                    ml: 0.5,
                    boxShadow: 2
                  }
                }}
                MenuListProps={{
                  sx: {
                    minWidth: 200,
                    py: 0.5,
                    maxHeight: 400,
                    overflow: 'auto',
                    '& .MuiMenuItem-root': {
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      fontSize: '0.875rem'
                    }
                  }
                }}
              >
                {clusters.map(c => (
                  <MenuItem 
                    key={c} 
                    onClick={() => handleClusterSelect(c)}
                    selected={hierarchyValue === c}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      fontSize: '0.875rem'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        margin: 0,
                        padding: 0,
                        width: '100%'
                      }}
                    >
                      {c}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Menu>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={variant.locale}
                onChange={(e) => onVariantChange({ ...variant, locale: e.target.value as string })}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  }
                }}
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Languages size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                    <span style={{ fontSize: '0.875rem' }}>{value}</span>
                  </Box>
                )}
              >
                {locales.map(l => (
                  <MenuItem key={l} value={l} sx={{ fontSize: '0.875rem' }}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<GitCompareArrows size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Compare
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<FileClock size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Audit
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<ClipboardCheck size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Checklist
              </Button>
            </span>
          </Tooltip>

          {onCancel && (
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              disabled={modifiedCount === 0}
              sx={{
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: 'error.main',
                ...(modifiedCount === 0 && { opacity: 0.5 })
              }}
            >
              Cancel
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
          onClick={async () => {
            if (!onSave) return;
            try { setSaving(true); await onSave(); } finally { setSaving(false); }
          }}
          disabled={!templateKey || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
            sx={{
              height: 32,
              fontSize: '0.875rem',
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {saving ? 'Saving…' : `Save${modifiedCount > 0 ? ` (${modifiedCount})` : ''}`}
          </Button>
        </Box>
      </Box>

      {/* Preview Header Section - Lower Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          flex: 1,
          minWidth: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={templateChannel} 
              size="small"
              sx={{ fontWeight: 600 }}
            />
            {displayPrefix && (
              <Chip
                label={displayPrefix}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  height: 20,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: theme => theme.palette.primary.main + '1A'
                }}
              />
            )}
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {nameWithoutPrefix || templateKey || 'Select a template'}
            </Typography>
          </Box>
          {templateChannel !== 'HER' && onPreviewModeChange && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
                bgcolor: 'background.default'
              }}
            >
              <Button
                variant={previewMode === 'desktop' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('desktop')}
                sx={{
                  minWidth: 28,
                  height: 28,
                  p: 0
                }}
              >
                <Monitor size={16} />
              </Button>
              <Tooltip title="This feature is coming soon">
                <span>
                  <Button
                    variant={previewMode === 'mobile' ? 'contained' : 'text'}
                    size="small"
                    onClick={() => {}} // No-op: functionality preserved but disabled
                    disabled={true}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      p: 0,
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    }}
                  >
                    <Smartphone size={16} />
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={hierarchyDisplayText} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={variant.locale} 
              size="small"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={elementKey}
                onChange={(e) => onElementChange(e.target.value as string)}
                disabled={!elements.length}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  }
                }}
                renderValue={(value) => {
                  const selected = (elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }])
                    .find(e => e.elementKey === value);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Library size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{selected?.displayName || value}</span>
                    </Box>
                  );
                }}
              >
                {(elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }]).map(e => (
                  <MenuItem key={e.elementKey} value={e.elementKey} sx={{ fontSize: '0.875rem' }}>
                    {e.elementKey === 'Full Template' && <Library size={16} style={{ marginRight: '8px' }} />}
                    {e.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              size="small" 
              sx={{ minWidth: 180 }}
              error={!!scenariosError}
            >
              <Select
                value={variant.scenario}
                onChange={(e) => {
                  // Store the scenario VALUE (e.g., "MS_1") in variant.scenario, which will be sent to API
                  // The label (e.g., "Default") is only for display in the UI
                  const selectedValue = e.target.value as string;
                  const selectedScenario = scenarios.find(s => s.value === selectedValue);
                  console.log('[EditorToolbar] Scenario selected:', {
                    label: selectedScenario?.label, // Displayed in UI
                    value: selectedValue, // Stored and sent to API (e.g., "MS_1")
                    willSendToAPI: selectedValue
                  });
                  onVariantChange({ ...variant, scenario: selectedValue });
                }}
                displayEmpty
                disabled={scenariosLoading || scenarios.length === 0}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  }
                }}
                renderValue={(value) => {
                  // Note: 'value' parameter is the scenario VALUE (e.g., "MS_1"), but we display the LABEL (e.g., "Default")
                  if (scenariosLoading) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Loading...</span>
                      </Box>
                    );
                  }
                  const selected = scenarios.find(s => s.value === value);
                  if (!selected && scenarios.length === 0) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Layers size={16} style={{ color: 'inherit', opacity: 0.5 }} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>No scenarios</span>
                      </Box>
                    );
                  }
                  // Display label in UI, but value (e.g., "MS_1") is stored in variant.scenario and sent to API
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{truncateText(selected?.label || value, 14)}</span>
                    </Box>
                  );
                }}
              >
                {scenariosLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Loading scenarios...</Typography>
                    </Box>
                  </MenuItem>
                ) : scenarios.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {scenariosError ? 'Error loading scenarios' : 'No scenarios available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  scenarios.map(s => (
                    <MenuItem 
                      key={s.value} 
                      value={s.value} 
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <Tooltip 
                        title={s.label.length > 20 ? s.label : ''}
                        placement="right"
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap', 
                            fontSize: '0.875rem',
                            width: '100%'
                          }}
                        >
                          {s.label}
                        </Typography>
                      </Tooltip>
                    </MenuItem>
                  ))
                )}
              </Select>
              {scenariosError && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.75 }}>
                  {scenariosError}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>
    </>
  );
}


